import React from 'react';
import { SendIcon, AutoAwesomeTwoToneIcon } from '@/components/icons';
import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  useTheme,
} from '@mui/material';

import { useChatBot, useGetConfig } from './ChatBot';
import { getIsMobile } from '../utils/device';
import { scaledFontSize } from './createWidgetTheme';
import {
  VoiceRecorder,
  type VoiceRecorderErrorReason,
  type VoiceRecorderState,
} from './VoiceRecorder';
import {
  VoicePrivacyDisclosure,
  acknowledgeVoicePrivacy,
  hasAcknowledgedVoicePrivacy,
} from './VoicePrivacyDisclosure';
import { WidgetNotice } from './WidgetNotice';
import { TranscribingPill } from './TranscribingPill';
import { IntakeLockedComposer } from './_parts/IntakeLockedComposer';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';
import { visuallyHiddenSx } from './visuallyHiddenSx';

interface ChatInputProps {
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  /** SEM-7139 — lock composer while intake form is shown */
  locked?: boolean;
  /**
   * Focus and mobile keyboard handling must not scroll the host page, and the
   * input must not take focus on mount. Used by the embedded panel.
   */
  preventPageScroll?: boolean;
}

export function ChatInput({
  inputRef: externalInputRef,
  locked = false,
  preventPageScroll = false,
}: ChatInputProps = {}) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const widgetScale =
    (theme as typeof theme & { widgetTypographyScale?: number })
      .widgetTypographyScale ?? 1;
  const hasScaledTypography = widgetScale !== 1;
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null
  );
  const {
    send,
    sendVoice,
    state,
    voiceError,
    dismissVoiceError,
    retryVoiceError,
    isTranscribingVoice,
    setIsVoiceActive,
  } = useChatBot();
  const config = useGetConfig();
  const [inputValue, setInputValue] = React.useState('');
  const [recorderState, setRecorderState] =
    React.useState<VoiceRecorderState>('idle');
  const [showVoicePrivacy, setShowVoicePrivacy] = React.useState(false);
  const [micErrorReason, setMicErrorReason] =
    React.useState<VoiceRecorderErrorReason | null>(null);
  const [transcribingStartedAt, setTranscribingStartedAt] = React.useState<
    number | null
  >(null);

  const hasValidInput = inputValue.trim().length > 0;
  const isWaitingForResponse = state === 'pending';
  const isRecording = recorderState === 'recording';
  const isTranscribing = recorderState === 'transcribing';
  // The recorder owns the input row in 'recording' and 'review' (it has
  // its own send/discard chrome) and in 'transcribing' (where we render
  // the pill instead of the text input). All three: do not show the
  // typing input or send button.
  const recorderOwnsRow = recorderState !== 'idle' && recorderState !== 'error';
  const canSend =
    hasValidInput && !isWaitingForResponse && !recorderOwnsRow && !locked;

  React.useEffect(() => {
    setIsVoiceActive(recorderOwnsRow);
  }, [recorderOwnsRow, setIsVoiceActive]);
  const showMicBlockedNotice = micErrorReason === 'mic_denied';
  const showPlaybackFailedNotice = micErrorReason === 'playback_failed';
  const showTranscribeFailedNotice =
    voiceError?.reason === 'transcribe_failed' ||
    voiceError?.reason === 'upload_failed';

  function onSend() {
    if (!canSend) return;

    send(inputValue.trim());
    setInputValue('');
    // Sending any message implicitly acknowledges the voice disclosure: we
    // never want it to keep popping up after the user has clearly engaged
    // with the chat surface. We also auto-dismiss any voice notices since
    // the user clearly chose to type instead of dealing with them.
    if (showVoicePrivacy) {
      acknowledgeVoicePrivacy();
      setShowVoicePrivacy(false);
    }
    if (micErrorReason) setMicErrorReason(null);
    if (voiceError) dismissVoiceError();
  }

  // Mirrors the dismissals onSend performs for typed messages: any voice
  // send is also an "I've engaged" signal, so it acknowledges the privacy
  // disclosure and clears any visible mic / transcription notices. We do
  // NOT reset recorderState here — VoiceRecorder.sendReview already moved
  // it to 'transcribing' and we want the TranscribingPill to render. The
  // effect below brings us back to 'idle' once the chat reply lands.
  const handleRecordingComplete = React.useCallback(
    (audioBlob: Blob) => {
      if (showVoicePrivacy) {
        acknowledgeVoicePrivacy();
        setShowVoicePrivacy(false);
      }
      if (micErrorReason) setMicErrorReason(null);
      if (voiceError) dismissVoiceError();
      sendVoice(audioBlob);
    },
    [sendVoice, showVoicePrivacy, micErrorReason, voiceError, dismissVoiceError]
  );

  // Show the privacy disclosure exactly once per browser session — the
  // first time the user taps the mic in this session. After they
  // acknowledge it, hasAcknowledgedVoicePrivacy() returns true for the
  // rest of the session (backed by sessionStorage), and the disclosure
  // resets naturally when the tab/browser is closed. We listen on
  // recorderState transitions because the VoiceRecorder is the surface
  // that owns the actual microphone tap and getUserMedia call. Starting
  // a new recording also auto-dismisses any error notices.
  const handleRecorderStateChange = React.useCallback(
    (next: VoiceRecorderState) => {
      if (
        recorderState === 'idle' &&
        next === 'recording' &&
        !hasAcknowledgedVoicePrivacy()
      ) {
        setShowVoicePrivacy(true);
      }
      if (next === 'recording') {
        setMicErrorReason(null);
        if (voiceError) dismissVoiceError();
      }
      if (next === 'transcribing') {
        setTranscribingStartedAt(Date.now());
      } else if (next === 'idle' || next === 'error') {
        setTranscribingStartedAt(null);
      }
      setRecorderState(next);
    },
    [recorderState, voiceError, dismissVoiceError]
  );

  const handleMicError = React.useCallback(
    (reason: VoiceRecorderErrorReason) => {
      setMicErrorReason(reason);
    },
    []
  );

  // When the chat panel remounts (e.g. close then reopen) ChatInput unmounts and
  // remounts, losing its local recorderState. Restore it from the shared
  // context flag so the TranscribingPill stays visible.
  React.useEffect(() => {
    if (isTranscribingVoice && recorderState === 'idle') {
      setRecorderState('transcribing');
      setTranscribingStartedAt(Date.now());
    }
  }, [isTranscribingVoice]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drop the "Transcribing…" pill the moment transcription itself ends —
  // not when the bot reply ends. The user's bubble already shows the full
  // transcript at that point, so leaving the pill up while the bot is
  // streaming back would be misleading ("Transcribing your voice note…"
  // when we're actually waiting on the AI). The bot-reply phase is
  // signalled separately via `state === 'pending'` (spinner + disabled
  // send in the standard input row).
  React.useEffect(() => {
    if (recorderState !== 'transcribing') return;
    if (voiceError) {
      setRecorderState('idle');
      setTranscribingStartedAt(null);
      return;
    }
    if (!isTranscribingVoice) {
      setRecorderState('idle');
      setTranscribingStartedAt(null);
    }
  }, [recorderState, isTranscribingVoice, voiceError]);

  const handleRetryVoice = React.useCallback(() => {
    setRecorderState('transcribing');
    setTranscribingStartedAt(Date.now());
    retryVoiceError();
  }, [retryVoiceError]);

  const prevStateRef = React.useRef(state);
  React.useEffect(() => {
    const wasPending = prevStateRef.current === 'pending';
    prevStateRef.current = state;
    if (state !== 'idle') return;
    if (!preventPageScroll) {
      inputRef.current?.focus();
      return;
    }
    // Only refocus after a reply, never on mount, so page load can't grab focus.
    if (wasPending) inputRef.current?.focus({ preventScroll: true });
  }, [state, preventPageScroll]);

  const handleFocus = () => {
    if (!preventPageScroll && getIsMobile() && containerRef.current) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }, 300);
    }
  };

  const browserSupportsRecording =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';
  // Mic UI is gated on BOTH the institution-level toggle and the browser
  // capability. Either one being false hides the mic entirely - including
  // skipping the privacy disclosure - per AC.
  const supportsRecording =
    !locked && browserSupportsRecording && Boolean(config.voiceInputEnabled);
  const voiceRecorder = supportsRecording ? (
    <VoiceRecorder
      onRecordingComplete={handleRecordingComplete}
      onError={handleMicError}
      sendDisabled={isWaitingForResponse}
      recorderState={recorderState}
      setRecorderState={handleRecorderStateChange}
    />
  ) : null;
  const inputChromeSx = {
    display: 'flex',
    alignItems: 'center',
    borderRadius: 4,
    boxShadow: 1,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[300]}`,
    minHeight: 48,
    px: 0.5,
    transition: 'all 0.2s ease-in-out',
  };

  return (
    <Box
      ref={containerRef}
      sx={{ position: 'relative', flex: 1, width: '100%' }}
    >
      {/* The message input is not rendered while locked, so its label and hint would be orphans. */}
      {!locked && (
        <>
          <Box
            id="chat-message-input-label"
            component="span"
            lang={locale}
            sx={visuallyHiddenSx}
          >
            {t('input.typeMessage')}
          </Box>
          <Box
            id="chat-input-hint"
            component="span"
            lang={locale}
            sx={visuallyHiddenSx}
          >
            {t('input.enterHint')}
          </Box>
        </>
      )}
      {showMicBlockedNotice && (
        <WidgetNotice
          variant="mic_blocked"
          onDismiss={() => setMicErrorReason(null)}
        />
      )}
      {showPlaybackFailedNotice && (
        <WidgetNotice
          variant="playback_failed"
          onDismiss={() => setMicErrorReason(null)}
        />
      )}
      {showTranscribeFailedNotice && (
        <WidgetNotice
          variant="transcribe_failed"
          body={
            voiceError?.reason === 'upload_failed'
              ? 'Something went wrong. Tap retry or type instead.'
              : undefined
          }
          onDismiss={dismissVoiceError}
          onRetry={handleRetryVoice}
        />
      )}
      {showVoicePrivacy && supportsRecording && (
        <VoicePrivacyDisclosure onDismiss={() => setShowVoicePrivacy(false)} />
      )}
      {locked ? (
        <IntakeLockedComposer />
      ) : isTranscribing ? (
        <Box sx={inputChromeSx}>
          <TranscribingPill startedAt={transcribingStartedAt ?? undefined} />
        </Box>
      ) : (
        // Single chrome Box for both idle and recording/review. We render
        // VoiceRecorder at a stable tree position so it does NOT unmount
        // when the recorder transitions from idle to recording. If we
        // moved it between trees, the new instance would mount with a
        // null mediaRecorderRef and the timer/Stop button would silently
        // break (the recording UI would show but its state would be
        // detached from the actually-running MediaRecorder).
        <Box sx={inputChromeSx}>
          <InputBase
            inputRef={element => {
              inputRef.current = element;
              if (externalInputRef) {
                externalInputRef.current = element;
              }
            }}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onFocus={handleFocus}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (canSend) {
                  onSend();
                }
              }
            }}
            disabled={recorderOwnsRow || locked}
            multiline
            fullWidth
            minRows={1}
            maxRows={6}
            inputProps={{
              id: 'chat-message-input',
              dir: 'auto',
              'aria-labelledby': 'chat-message-input-label',
              'aria-describedby': 'chat-input-hint',
              'aria-disabled': locked || undefined,
            }}
            sx={{
              display: recorderOwnsRow ? 'none' : 'flex',
              flex: 1,
              p: 1.5,
              ...(locked ? { opacity: 0.55 } : {}),
              ...(hasScaledTypography
                ? {
                    '& .MuiInputBase-input': {
                      fontSize: scaledFontSize(theme, 16),
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: scaledFontSize(theme, 16),
                    },
                    '& .MuiInputBase-inputMultiline': {
                      fontSize: scaledFontSize(theme, 16),
                    },
                    '& .MuiInputBase-inputMultiline::placeholder': {
                      fontSize: scaledFontSize(theme, 16),
                    },
                  }
                : {}),
            }}
            placeholder={
              locked
                ? 'Complete the form above to continue…'
                : isRecording
                  ? 'Recording...'
                  : 'Ask a question...'
            }
            startAdornment={
              <Box sx={{ display: 'flex', alignItems: 'center', pl: 1, pr: 1 }}>
                {isWaitingForResponse ? (
                  <CircularProgress
                    size={18}
                    thickness={4}
                    lang={locale}
                    aria-label={t('input.waitingForResponse')}
                  />
                ) : (
                  <AutoAwesomeTwoToneIcon
                    color="disabled"
                    fontSize="small"
                    aria-hidden
                  />
                )}
              </Box>
            }
          />
          {voiceRecorder && (
            <Box
              sx={{
                display: !recorderOwnsRow && hasValidInput ? 'none' : 'flex',
                alignItems: 'center',
                alignSelf: recorderOwnsRow ? 'stretch' : 'flex-end',
                ...(recorderOwnsRow ? { flex: 1, minWidth: 0 } : { mb: 0.5 }),
              }}
            >
              {voiceRecorder}
            </Box>
          )}
          {!recorderOwnsRow && (
            <IconButton
              onClick={onSend}
              disabled={!canSend}
              lang={locale}
              aria-label={
                canSend ? t('input.sendMessage') : t('input.sendMessageDisabled')
              }
              title={canSend ? t('input.sendMessage') : t('input.typeToSend')}
              sx={{
                height: 32,
                minWidth: 64,
                borderRadius: 16,
                textTransform: 'none',
                alignSelf: 'flex-end',
                mb: 0.5,
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <SendIcon color={canSend ? 'primary' : 'disabled'} aria-hidden />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
}
